import React from 'react';
import { X } from 'lucide-react';
import { DashboardTab } from '../../types/tabs.types';

interface TabItemProps {
  tab: DashboardTab;
  isRenaming: boolean;
  renameValue: string;
  onTabSwitch: (tabId: string) => void;
  onTabContextMenu: (event: React.MouseEvent, tabId: string) => void;
  onTabDelete: (tabId: string) => void;
  onRenameValueChange: (value: string) => void;
  onRenameFinish: () => void;
  onRenameKeyDown: (event: React.KeyboardEvent) => void;
  renameInputRef: React.RefObject<any>;
  canDelete: boolean;
  isDragging?: boolean;
  dropPosition?: 'left' | 'right' | null;
  onDragStart: (tabId: string) => void;
  onDragEnd: () => void;
  onDragOver: (tabId: string, position: 'left' | 'right') => void;
  onDrop: (tabId: string, position: 'left' | 'right') => void;
  maxWidth?: number;
}

const TabItem: React.FC<TabItemProps> = ({
  tab,
  isRenaming,
  renameValue,
  onTabSwitch,
  onTabContextMenu,
  onTabDelete,
  onRenameValueChange,
  onRenameFinish,
  onRenameKeyDown,
  renameInputRef,
  canDelete,
  isDragging = false,
  dropPosition = null,
  onDragStart,
  onDragEnd,
  onDragOver,
  onDrop,
  maxWidth = 200,
}) => {
  const handleDragStart = (e: React.DragEvent) => {
    e.stopPropagation();
    e.dataTransfer.effectAllowed = 'move';
    e.dataTransfer.setData('text/plain', tab.id);
    onDragStart(tab.id);
  };

  const handleDragEnd = (e: React.DragEvent) => {
    e.stopPropagation();
    onDragEnd();
  };

  const handleDragOver = (e: React.DragEvent) => {
    e.preventDefault();
    e.stopPropagation();
    e.dataTransfer.dropEffect = 'move';

    // Calculate position based on mouse position within the tab
    const rect = e.currentTarget.getBoundingClientRect();
    const mouseX = e.clientX;
    const tabCenter = rect.left + rect.width / 2;
    const position = mouseX < tabCenter ? 'left' : 'right';

    onDragOver(tab.id, position);
  };

  const handleDragEnter = (e: React.DragEvent) => {
    e.preventDefault();
    e.stopPropagation();
  };

  const handleDrop = (e: React.DragEvent) => {
    e.preventDefault();
    e.stopPropagation();

    // Calculate drop position based on mouse position within the tab
    const rect = e.currentTarget.getBoundingClientRect();
    const mouseX = e.clientX;
    const tabCenter = rect.left + rect.width / 2;
    const position = mouseX < tabCenter ? 'left' : 'right';

    onDrop(tab.id, position);
  };

  const handleClick = () => {
    // Only switch tabs if we're not in the middle of a drag operation
    if (!isDragging) {
      onTabSwitch(tab.id);
    }
  };
  return (
    <div className="relative group tab-item">
      {isRenaming ? (
        <div
          className={`
            relative bg-white dark:bg-gray-900 
            rounded-t-lg border-t border-l border-r border-gray-200 dark:border-gray-700
            px-3 py-2 ${tab.isActive ? 'border-b-0 z-10' : ''}
          `}
          style={{
            maxWidth: `${maxWidth}px`,
            minWidth: `${Math.min(60, maxWidth)}px`,
          }}
        >
          <input
            ref={renameInputRef}
            type="text"
            value={renameValue}
            onChange={(e) => onRenameValueChange(e.target.value)}
            onBlur={onRenameFinish}
            onKeyDown={onRenameKeyDown}
            className="w-full text-sm bg-transparent border-none focus:outline-none focus:ring-0 text-gray-900 dark:text-gray-100"
          />
        </div>
      ) : (
        <div
          draggable={!isRenaming}
          onDragStart={handleDragStart}
          onDragEnd={handleDragEnd}
          onDragOver={handleDragOver}
          onDragEnter={handleDragEnter}
          onDrop={handleDrop}
          className={`
            relative flex items-center transition-all duration-200 
            rounded-t-lg border-t border-l border-r border-gray-200 dark:border-gray-700
            ${isDragging ? 'opacity-50 rotate-1 scale-105 cursor-grabbing' : 'cursor-grab hover:cursor-grab'}
            ${
              tab.isActive
                ? 'bg-white dark:bg-gray-900 border-b-0 z-10'
                : 'bg-gray-100 dark:bg-gray-700 hover:bg-gray-50 dark:hover:bg-gray-650 border-b border-gray-200 dark:border-gray-700'
            }
          `}
          style={{
            maxWidth: `${maxWidth}px`,
            minWidth: `${Math.min(60, maxWidth)}px`,
          }}
          onClick={handleClick}
          onContextMenu={(e) => onTabContextMenu(e, tab.id)}
        >
          {/* Drop zone indicators */}
          {dropPosition === 'left' && (
            <div className="absolute left-0 top-0 bottom-0 w-1 bg-[#E34290] z-20"></div>
          )}
          {dropPosition === 'right' && (
            <div className="absolute right-0 top-0 bottom-0 w-1 bg-[#E34290] z-20"></div>
          )}

          {/* Pink indicator dot for active tab */}
          {tab.isActive && (
            <div
              className={`absolute ${maxWidth < 100 ? 'left-2' : 'left-3'} top-1/2 transform -translate-y-1/2 w-2 h-2 bg-[#E34290] rounded-full`}
            ></div>
          )}

          {/* Tab content */}
          <div
            className={`flex items-center justify-between w-full py-2 ${maxWidth < 100 ? 'px-2' : 'px-3'}`}
          >
            <span
              className={`
                ${maxWidth < 80 ? 'text-xs' : 'text-sm'} truncate flex-1 select-none
                ${
                  tab.isActive
                    ? `text-gray-900 dark:text-gray-100 font-medium ${maxWidth < 100 ? 'ml-2' : 'ml-4'}`
                    : 'text-gray-600 dark:text-gray-400'
                }
              `}
            >
              {tab.name}
            </span>

            {/* Close button - hide on very small tabs */}
            {canDelete && maxWidth > 120 && (
              <button
                onClick={(e) => {
                  e.stopPropagation();
                  onTabDelete(tab.id);
                }}
                className={`
                  ml-2 p-1 rounded-sm transition-colors
                  ${
                    tab.isActive
                      ? 'hover:bg-gray-100 dark:hover:bg-gray-700 text-gray-500 dark:text-gray-400'
                      : 'hover:bg-gray-200 dark:hover:bg-gray-600 text-gray-400 dark:text-gray-500'
                  }
                  opacity-0 group-hover:opacity-100
                `}
              >
                <X className="h-3 w-3" />
              </button>
            )}
          </div>
        </div>
      )}
    </div>
  );
};

export default TabItem;
